// packages/web/src/components/TennisRatingSlider.tsx
import React from 'react';
import {
  Box,
  Typography,
  Slider,
  FormControl,
  FormLabel,
  Chip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { PlayerRatings, UTR_MIN, UTR_MAX, NTRP_MIN, NTRP_MAX, formatUTRRating, formatNTRPRating } from '@matchpoint/shared';

const RatingContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const RatingHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(1),
}));

const RatingDescription = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(2),
}));

interface TennisRatingSliderProps {
  ratings: PlayerRatings;
  onChange: (ratings: PlayerRatings) => void;
  disabled?: boolean;
}

export const TennisRatingSlider: React.FC<TennisRatingSliderProps> = ({
  ratings,
  onChange,
  disabled = false
}) => {
  const handleUTRChange = (event: Event, newValue: number | number[]) => {
    const value = Array.isArray(newValue) ? newValue[0] : newValue;
    onChange({
      ...ratings,
      utr: value === UTR_MIN ? null : value
    });
  };

  const handleNTRPChange = (event: Event, newValue: number | number[]) => {
    const value = Array.isArray(newValue) ? newValue[0] : newValue;
    onChange({
      ...ratings,
      ntrp: value === NTRP_MIN ? null : value
    });
  };

  // UTR marks
  const utrMarks = [
    { value: UTR_MIN, label: 'Not Rated' },
    { value: 2, label: '2.0' },
    { value: 4, label: '4.0' },
    { value: 6, label: '6.0' },
    { value: 8, label: '8.0' },
    { value: 10, label: '10.0' },
    { value: 12, label: '12.0' },
    { value: 14, label: '14.0' },
    { value: UTR_MAX, label: '16.5' },
  ];

  // NTRP marks
  const ntrpMarks = [
    { value: NTRP_MIN, label: 'Not Rated' },
    { value: 2, label: '2.0' },
    { value: 3, label: '3.0' },
    { value: 4, label: '4.0' },
    { value: 5, label: '5.0' },
    { value: 6, label: '6.0' },
    { value: NTRP_MAX, label: '7.0' },
  ];

  return (
    <Box>
      {/* UTR Rating */}
      <RatingContainer>
        <FormControl component="fieldset" fullWidth>
          <RatingHeader>
            <FormLabel component="legend">
              <Typography variant="subtitle1" fontWeight="bold">
                UTR (Universal Tennis Rating)
              </Typography>
            </FormLabel>
            <Chip 
              label={formatUTRRating(ratings.utr)} 
              color={ratings.utr ? 'primary' : 'default'}
              size="small"
            />
          </RatingHeader>
          
          <RatingDescription>
            UTR is a 16-point global rating system that promotes fair and competitive play across all levels.
            Move the slider to "Not Rated" if you don't have a UTR rating.
          </RatingDescription>
          
          <Slider
            value={ratings.utr || UTR_MIN}
            onChange={handleUTRChange}
            disabled={disabled}
            min={UTR_MIN}
            max={UTR_MAX}
            step={0.1}
            marks={utrMarks}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => value === UTR_MIN ? 'Not Rated' : value.toFixed(1)}
            sx={{
              '& .MuiSlider-markLabel': {
                fontSize: '0.75rem',
              },
            }}
          />
        </FormControl>
      </RatingContainer>

      {/* NTRP Rating */}
      <RatingContainer>
        <FormControl component="fieldset" fullWidth>
          <RatingHeader>
            <FormLabel component="legend">
              <Typography variant="subtitle1" fontWeight="bold">
                NTRP (National Tennis Rating Program)
              </Typography>
            </FormLabel>
            <Chip 
              label={formatNTRPRating(ratings.ntrp)} 
              color={ratings.ntrp ? 'secondary' : 'default'}
              size="small"
            />
          </RatingHeader>
          
          <RatingDescription>
            NTRP is the official rating system of the USTA, ranging from 1.0 (beginner) to 7.0 (world-class).
            Move the slider to "Not Rated" if you don't have an NTRP rating.
          </RatingDescription>
          
          <Slider
            value={ratings.ntrp || NTRP_MIN}
            onChange={handleNTRPChange}
            disabled={disabled}
            min={NTRP_MIN}
            max={NTRP_MAX}
            step={0.1}
            marks={ntrpMarks}
            valueLabelDisplay="auto"
            valueLabelFormat={(value) => value === NTRP_MIN ? 'Not Rated' : value.toFixed(1)}
            sx={{
              '& .MuiSlider-markLabel': {
                fontSize: '0.75rem',
              },
            }}
          />
        </FormControl>
      </RatingContainer>
    </Box>
  );
};